import React, { useState, useEffect } from "react";
import { Moon, Sun, Menu, X } from "lucide-react";
import { Button } from "./ui/button";

export const Header = () => {
  const [isDark, setIsDark] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDark]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setIsMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 dark:bg-[#0F0F0F]/95 backdrop-blur-md shadow-md"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center hover:opacity-80 transition-opacity"
              aria-label="Scroll to top"
            >
              <img
                src="/img/logo/logo.png"
                alt="Dreams Marketing"
                className="h-10 w-auto md:h-12 lg:h-14 object-contain"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection("layanan")}
              className="text-gray-700 dark:text-gray-300 hover:text-[#D32F2F] dark:hover:text-[#D32F2F] transition-colors font-medium"
            >
              Layanan
            </button>
            <button
              onClick={() => scrollToSection("trial-demo")}
              className="text-gray-700 dark:text-gray-300 hover:text-[#D32F2F] dark:hover:text-[#D32F2F] transition-colors font-medium"
            >
              Trial Demo
            </button>
            <button
              onClick={() => scrollToSection("diantosan")}
              className="text-gray-700 dark:text-gray-300 hover:text-[#D32F2F] dark:hover:text-[#D32F2F] transition-colors font-medium"
            >
              Diantosan
            </button>
            <button
              onClick={() => scrollToSection("tentang")}
              className="text-gray-700 dark:text-gray-300 hover:text-[#D32F2F] dark:hover:text-[#D32F2F] transition-colors font-medium"
            >
              Tentang
            </button>
            <button
              onClick={() => scrollToSection("tim")}
              className="text-gray-700 dark:text-gray-300 hover:text-[#D32F2F] dark:hover:text-[#D32F2F] transition-colors font-medium"
            >
              Tim
            </button>
          </nav>

          {/* CTA & Theme Toggle */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              onClick={() => scrollToSection("kontak")}
              className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white transition-colors"
            >
              Konsultasi Gratis
            </Button>
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              ) : (
                <Moon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              )}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center space-x-2">
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              ) : (
                <Moon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              )}
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 dark:border-gray-700">
            <nav className="flex flex-col space-y-3">
              <button
                onClick={() => scrollToSection("layanan")}
                className="text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Layanan
              </button>
              <button
                onClick={() => scrollToSection("trial-demo")}
                className="text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Trial Demo
              </button>
              <button
                onClick={() => scrollToSection("diantosan")}
                className="text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Diantosan
              </button>
              <button
                onClick={() => scrollToSection("tentang")}
                className="text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Tentang
              </button>
              <button
                onClick={() => scrollToSection("tim")}
                className="text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Tim
              </button>
              <Button
                onClick={() => scrollToSection("kontak")}
                className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white w-full"
              >
                Konsultasi Gratis
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
